import React, { useEffect, useRef, useState } from 'react';
import { styled } from '@mui/material/styles';
import {
  ProductListItemDto,
  CompanyScoringState,
} from 'models/dtoTypes';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import Tooltip from '@mui/material/Tooltip';
import {
  GetProductListItems,
  TriggerRescoringProductListItems,
  // GetProductListStatus,
} from 'services/productList';
import { BulkStatusDto } from 'models/dtoTypes';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import { ICountryOption } from '.';
import Box from '@mui/material/Box';
import ResourceTable, { IColumn, IResourceTableRef } from 'kosmos/components/ResourceTable';
import { IFilterField } from 'models/types';
import { TFunction, useTranslation } from 'react-i18next';
import BulkStatusToolbar from './_bulkStatusToolbar';
import { CurrentUser } from 'auth';
import generateNonce from 'utils/generateNonce';
const PREFIX = 'ProductListVerificationTable';

const classes = {
  root: `${PREFIX}-root`,
  actions: `${PREFIX}-actions`,
  pagination: `${PREFIX}-pagination`,
  toolbar: `${PREFIX}-toolbar`,
};

const Root = styled('div')(() => ({
  position: 'relative',
  width: '100%',
  [`& .${classes.toolbar}`]: {
    display: 'flex',
    gap: 16,
    flexDirection: 'row',
  },
  [`& .${classes.pagination}`]: {
    paddingTop: 10,
    paddingBottom: 10,
  },
}));

interface IProps {
  countries: ICountryOption[];
  onErrorResponse: (e: unknown) => void;
  productListId: string;
  columns: IColumn<ProductListItemDto>[];
}

const filterSchema = (t: TFunction): IFilterField[] => {
  return [
    {
      label: t('name'),
      name: 'name',
      widget: 'text',
    },
    {
      label: t('Transaction Id'),
      name: 'transactionId',
      widget: 'text',
    },
    {
      label: t('scoring-status'),
      name: 'scoringState',
      widget: 'select',
      options: [
        { label: t('in-progress'), alias: CompanyScoringState.InProgress },
        { label: t('error'), alias: CompanyScoringState.Error },
        { label: t('completed'), alias: CompanyScoringState.Completed },
      ],
    },
  ];
};

export default function ProductListVerificationTable(props: IProps) {
  const { columns, productListId, onErrorResponse } = props;
  const isAdmin = CurrentUser.getInstance().isAdmin();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const tableRef = useRef<IResourceTableRef>(null);
  const { t } = useTranslation('default');
  const [scoringSelected, setScoringSelected] = React.useState<boolean>(false);
  const [nonce, setNonce] = React.useState(generateNonce());
  const [bulkStatus, setBulkStatus] = useState<BulkStatusDto | null>({});
  const filteredColumns = columns.filter((a) => {
    if ((isAdmin && a.label === '') || a.label !== '') return a;
  });

  useEffect(() => {
    fetchBulkStatus();
  }, [productListId]);

  const fetchBulkStatus = () => {
    try {
      // const status = await GetProductListStatus(productListId);
      setBulkStatus(null);
    } catch (e) {
      onErrorResponse(e);
    }
  };

  const handleCheckboxChange = (id: string, isChecked: boolean) => {
    setSelectedIds((prevSelected) =>
      isChecked ? [...prevSelected, id] : prevSelected.filter((selectedId) => selectedId !== id),
    );
  };

  const canOpenSearchModal = (item: ProductListItemDto) => {
    console.log(item);
    return false;
  };

  const handleSelectRow = (item: ProductListItemDto) => {
    console.log(item);
  };

  const handleReScoreSelected = async () => {
    setScoringSelected(true);
    try {
      await TriggerRescoringProductListItems(selectedIds, productListId);
      console.log('selectedIds', selectedIds);
      setNonce(generateNonce());
    } catch (e) {
      onErrorResponse(e);
    }
    setScoringSelected(false);
  };

  const handleRefresh = () => {
    setSelectedIds([]);
    tableRef.current?.loadResources(0);
    fetchBulkStatus();
  };

  const renderReScoreSelectedButton = () => {
    if (!isAdmin) {
      return <></>;
    }
    if (selectedIds.length === 0) {
      const message = 'Please select companies to rescore.';
      return (
        <Tooltip title={message}>
          <Button color="warning" variant="outlined" startIcon={<PlayCircleIcon />}>
            {t('Retry')}
          </Button>
        </Tooltip>
      );
    } else {
      return (
        <LoadingButton
          loading={scoringSelected}
          loadingPosition="start"
          color="info"
          onClick={handleReScoreSelected}
          variant="contained"
          startIcon={<PlayCircleIcon />}
        >
          {t('Retry')}
        </LoadingButton>
      );
    }
  };

  const renderTopButtons = () => {
    return (
      <div className={classes.toolbar}>
        <div style={{ flex: 1 }}>
          <BulkStatusToolbar bulkStatus={bulkStatus} />
        </div>
        <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mb: 2 }}>
          {renderReScoreSelectedButton()}
          <Button
            color="primary"
            onClick={handleRefresh}
            variant="contained"
            startIcon={<RefreshIcon />}
          >
            {t('refresh')}
          </Button>
        </Stack>
      </div>
    );
  };

  return (
    <Root>
      {renderTopButtons()}
      <ResourceTable<ProductListItemDto>
        key={nonce}
        ref={tableRef}
        stickyHeader={true}
        filterSchema={filterSchema(t)}
        columns={filteredColumns}
        onFetch={(options) => GetProductListItems(productListId, options)}
        onFilterChanged={() => setSelectedIds([])}
        isRowSelectable={canOpenSearchModal}
        onSelectRow={handleSelectRow}
        selectedIds={selectedIds}
        onCheckboxChange={handleCheckboxChange}
        isAdmin={isAdmin}
        defaultSort={{
          sortColumn: 'position',
          isDesc: false,
        }}
      />
      <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
        <Box sx={{ flex: '1 1 auto' }} />
        {/* <BulkCsvDownloadButton productListId={productListId} /> */}
      </Box>
    </Root>
  );
}
